import { createSelector } from '@reduxjs/toolkit';
import { Store } from 'components/common/types/Store.types';
import { DirectSalesState, DirectSalesStatusOption } from 'components/common/types/DirectSalesCampaign.types';
import { isEmpty } from 'lodash';
import { isDirectSalesFailedStatus } from 'components/pages/Planner/hooks/utils/directSalesFailedStatus';
import { isDirectSalesLinePriced } from 'components/pages/Planner/hooks/utils/getIsDirectSalesLinePriced';
import { PERSISTED_STATUSES } from 'consts/campaignStatus';
import { getIsDraftStatus } from 'components/pages/Planner/PlannerLineActions/DirectSalesPlannerLineActions/utils';
import { getEveryLineState } from '../selectors';
import { getCampaignStatus } from './getCampaignStatus';

const getOptionState = (activeStates: DirectSalesState[]): DirectSalesState => {
  const [singleActiveState] = activeStates;
  const isEveryOptionWithDropDate = activeStates.every(
    (state) => state.expires instanceof Date && !Number.isNaN(state.expires.getTime()),
  );

  if (!isEveryOptionWithDropDate) return { status: DirectSalesStatusOption.OPTION };

  const isEveryLineDropDateEqual = activeStates.every(
    (state) => (state.expires as Date).getTime() === (singleActiveState.expires as Date).getTime(),
  );

  if (isEveryLineDropDateEqual) return singleActiveState;

  return { status: DirectSalesStatusOption.OPTION };
};

const getCampaignState = (data: DirectSalesState[]): DirectSalesState => {
  if (!data.length) return { status: DirectSalesStatusOption.DRAFT_UPDATING };

  const activeStates = data.filter((state) => state.status !== DirectSalesStatusOption.CANCELLED);
  const isEveryStateCancelled = !activeStates.length;

  if (isEveryStateCancelled) return { status: DirectSalesStatusOption.CANCELLED };

  const [singleActiveState] = activeStates;
  const isEveryStateOption = activeStates.every((state) => state.status === DirectSalesStatusOption.OPTION);

  if (isEveryStateOption) return getOptionState(activeStates);

  const isEveryStateEqual = activeStates.every((state) => state.status === singleActiveState.status);

  if (isEveryStateEqual) return singleActiveState;

  const isAnyStateLive = activeStates.some((state) => state.status === DirectSalesStatusOption.LIVE);

  if (isAnyStateLive) return { status: DirectSalesStatusOption.LIVE };

  const isEveryStateDraft = activeStates.every((state) => state.status.includes('DRAFT'));

  if (isEveryStateDraft) return { status: DirectSalesStatusOption.DRAFT };

  return { status: DirectSalesStatusOption.MIXED };
};

const getFilteredTransientCampaignStates = (
  mappedStates: DirectSalesState[],
  everyLineState: DirectSalesState[],
): DirectSalesState[] => {
  const hasFailedSolveOrPrice = everyLineState.some(({ status }) => isDirectSalesFailedStatus(status));
  const hasNonPricedLines = everyLineState.some(({ status }) => !isDirectSalesLinePriced(status));

  if (hasFailedSolveOrPrice || hasNonPricedLines) return everyLineState;

  const filteredWithoutDraftAndEnded = mappedStates.filter(
    ({ status }) => !getIsDraftStatus(status) && status !== DirectSalesStatusOption.ENDED,
  );

  const hasActiveNonCancelledStates = filteredWithoutDraftAndEnded.some(
    ({ status }) => status !== DirectSalesStatusOption.CANCELLED,
  );

  if (hasActiveNonCancelledStates) return filteredWithoutDraftAndEnded;

  return mappedStates.filter(({ status }) => status !== DirectSalesStatusOption.ENDED);
};

export const getDirectSalesCampaignState = createSelector(
  getEveryLineState,
  getCampaignStatus,
  (state: Store) => state.dealManagement.directSales.transientLineStates,
  (everyLineState, campaignStatus, transientLineStates) => {
    if (isEmpty(transientLineStates)) {
      if (campaignStatus === DirectSalesStatusOption.OPTION) {
        return { campaignState: getOptionState(everyLineState) };
      }

      if (campaignStatus?.includes('DRAFT')) {
        return { campaignState: getCampaignState(everyLineState) };
      }

      return { campaignState: { status: campaignStatus } };
    }

    const mappedTransientForCampaignToDealLineStates = Object.entries(transientLineStates).map(
      ([, { status, expires }], idx) => {
        const originalStatus = everyLineState[idx]?.status;

        return {
          status: originalStatus && PERSISTED_STATUSES.includes(originalStatus) ? originalStatus : status,
          expires,
        };
      },
    );

    const filteredTransientCampaignStates = getFilteredTransientCampaignStates(
      mappedTransientForCampaignToDealLineStates,
      everyLineState,
    );

    return {
      campaignState: getCampaignState(mappedTransientForCampaignToDealLineStates),
      transientCampaignStateOption: getCampaignState(filteredTransientCampaignStates),
    };
  },
);

export const getDirectSalesCampaignStatus = createSelector(
  getDirectSalesCampaignState,
  ({ campaignState }) => campaignState.status,
);
